import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Visibility,
  VisibilityOff,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import { SettingsShell } from './settings/SettingsShell';

interface StripeAccount {
  id?: string;
  accountId?: string;
  accountName?: string;
  email?: string;
  businessProfile?: string;
  country?: string;
  defaultCurrency?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
}

export const SettingsBillingPage = () => {
  const { tenantId } = useTenant();
  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [secretKeyConfigured, setSecretKeyConfigured] = useState(false);
  const [webhookSecretConfigured, setWebhookSecretConfigured] = useState(false);
  const [editingSecretKey, setEditingSecretKey] = useState(false);
  const [editingWebhookSecret, setEditingWebhookSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadPaymentSettings() {
      if (!tenantId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
        const stripe = tenantSnap.data()?.stripe || {};
        if (!mounted) return;
        setPublishableKey(stripe.publishableKey || '');
        setSecretKey('');
        setWebhookSecret('');
        setShowSecretKey(false);
        setShowWebhookSecret(false);
        setSecretKeyConfigured(Boolean(stripe.secretKeyRef));
        setWebhookSecretConfigured(Boolean(stripe.webhookSecret || stripe.webhookSecretRef));
        setEditingSecretKey(false);
        setEditingWebhookSecret(false);
      } catch (error) {
        console.error('Error loading payment settings:', error);
        setMessage({ type: 'error', text: 'Failed to load payment settings.' });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadPaymentSettings();
    return () => {
      mounted = false;
    };
  }, [tenantId]);

  const handleSave = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found.' });
      return;
    }

    if (!publishableKey.trim()) {
      setMessage({ type: 'error', text: 'Publishable key is required.' });
      return;
    }

    const trimmedSecretKey = secretKey.trim();
    const trimmedWebhookSecret = webhookSecret.trim();

    if (!secretKeyConfigured && !trimmedSecretKey) {
      setMessage({ type: 'error', text: 'Secret key is required until Stripe is configured.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const saveStripeKeys = httpsCallable(functions, 'saveStripeKeys');
      await saveStripeKeys({
        tenantId,
        publishableKey: publishableKey.trim(),
        ...(trimmedSecretKey ? { secretKey: trimmedSecretKey } : {}),
        ...(trimmedWebhookSecret ? { webhookSecret: trimmedWebhookSecret } : {}),
      });

      setSecretKey('');
      setWebhookSecret('');
      setShowSecretKey(false);
      setShowWebhookSecret(false);
      setSecretKeyConfigured(true);
      setWebhookSecretConfigured((current) => current || Boolean(trimmedWebhookSecret));
      setEditingSecretKey(false);
      setEditingWebhookSecret(false);
      setMessage({ type: 'success', text: 'Payment settings saved.' });
    } catch (error: any) {
      console.error('Error saving payment settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save payment settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found.' });
      return;
    }

    setVerifying(true);
    setMessage(null);
    try {
      const verifyStripeKeys = httpsCallable(functions, 'verifyStripeKeys');
      const result = await verifyStripeKeys({ tenantId });
      setStripeAccount(result.data as StripeAccount);
      setMessage({ type: 'success', text: 'Stripe keys verified.' });
    } catch (error: any) {
      console.error('Error verifying Stripe keys:', error);
      setStripeAccount(null);
      setMessage({ type: 'error', text: error.message || 'Failed to verify Stripe keys.' });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <SettingsShell title="Payment Settings">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>
              Stripe
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Configure the tenant Stripe keys used for checkout, subscriptions, and webhook
              verification.
            </Typography>

            <Stack spacing={2.5}>
              <TextField
                fullWidth
                required
                label="Publishable key"
                placeholder="pk_test_..."
                value={publishableKey}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setPublishableKey(event.target.value)}
                helperText="Starts with pk_test_ or pk_live_."
              />

              {(!secretKeyConfigured || editingSecretKey) && (
                <TextField
                  fullWidth
                  required={!secretKeyConfigured}
                  label="Secret key"
                  placeholder={secretKeyConfigured ? 'Enter new secret key' : 'sk_test_...'}
                  type={showSecretKey ? 'text' : 'password'}
                  value={secretKey}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setSecretKey(event.target.value)}
                  helperText={
                    secretKeyConfigured
                      ? 'Stored securely in Google Secret Manager. Enter a new value only to replace it.'
                      : 'Starts with sk_test_ or sk_live_.'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowSecretKey((current) => !current)} edge="end">
                          {showSecretKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {secretKeyConfigured && !editingSecretKey && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    Secret key
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Typography fontWeight={700}>Configured</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setSecretKey('');
                        setShowSecretKey(false);
                        setEditingSecretKey(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Stored securely in Google Secret Manager.
                  </Typography>
                </Box>
              )}

              {(!webhookSecretConfigured || editingWebhookSecret) && (
                <TextField
                  fullWidth
                  label="Webhook signing secret"
                  placeholder="whsec_..."
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={webhookSecret}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setWebhookSecret(event.target.value)}
                  helperText={
                    webhookSecretConfigured
                      ? 'Enter a new webhook signing secret only to replace it.'
                      : 'Stripe webhook signing secret used to verify incoming webhook events.'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowWebhookSecret((current) => !current)}
                          edge="end"
                        >
                          {showWebhookSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {webhookSecretConfigured && !editingWebhookSecret && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    Webhook signing secret
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Typography fontWeight={700}>Configured</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setWebhookSecret('');
                        setShowWebhookSecret(false);
                        setEditingWebhookSecret(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Stored for Stripe webhook verification.
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {stripeAccount && (
            <Alert severity="success" icon={<VerifiedIcon />}>
              {stripeAccount.accountName || stripeAccount.businessProfile || stripeAccount.email || stripeAccount.accountId || stripeAccount.id || 'Stripe account verified'}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? undefined : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Payment Settings'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={verifying ? undefined : <VerifiedIcon />}
              onClick={handleVerify}
              disabled={verifying || !secretKeyConfigured}
            >
              {verifying ? <CircularProgress size={24} /> : 'Verify Stripe Keys'}
            </Button>
          </Stack>
        </Stack>
      )}
    </SettingsShell>
  );
};
